import React from 'react';

import '../css/Payment.scss';
import GenericBuildingBlocks from 'app/components/customer/steps/Payment/Generic/GenericBuildingBlocks';
import GenericOnboardingServices from 'app/components/customer/steps/Payment/Generic/GenericOnboardingServices';
import nextBillingCycle from 'app/helpers/nextBillingCycle';
import '../css/GenericPaymentPage.scss';
import PriceCalculator from 'app/helpers/priceCalculator';
import { CheckoutHelper } from 'app/types/admin/customerUser';
import { AvailableProducts } from 'app/constants/Products';
import { Badge } from 'mui';
import { ImagesMapping } from 'app/components/customer/steps/Payment/king/ProductImage';
import protocolNames from 'app/utils/protocolNames';
import subProductNames from 'app/utils/subProductNames';
import PriceFormatter from 'app/components/customer/steps/Payment/Generic/PriceFormatter';
import cx from 'classnames';
import { useExperiment } from 'app/utils/useExperiment';

type Props = {
  currentProductName: AvailableProducts;
  multimonthPeriod: string;
  multimonthPlan: number;
  subProductName: string;
  checkoutHelper: CheckoutHelper;
  isOnboarding: boolean;
  supplementsOnAnotherIntake: boolean;
  activeProductWithSupplementName: string | undefined;
  productChanged: boolean;
  priceCalculator: PriceCalculator;
  doseType: string;
};

const SubscriptionWidget = ({
  currentProductName,
  priceCalculator,
  multimonthPeriod,
  multimonthPlan,
  isOnboarding,
  supplementsOnAnotherIntake,
  activeProductWithSupplementName,
  checkoutHelper,
  productChanged,
  subProductName,
  doseType,
}: Props) => {
  const SubscriptionSummaryComponent = checkoutHelper.subscriptionSummaryComponent();
  const starterPack = checkoutHelper.productSpecificInitialValues()['starter_pack'];

  const firstInstallmentDiscount =
    useExperiment('testosterone_first_installment_discount', 'testosterone_first_installment_discount_variation_0') ===
      'testosterone_first_installment_discount_variation_1' &&
    isOnboarding &&
    currentProductName === AvailableProducts.King;

  const discountPercentages = {
    12: '100%',
    3: '50%',
  };

  const firstInstallmentDescriptionText = {
    12: 'A year of personalized treatment from Maximus.',
    3: '3 months of expert treatment on Maximus.',
    1: 'Month-to-month expert treatment on Maximus.',
  };

  const firstInstallmentDiscountText = firstInstallmentDiscount ? discountPercentages[multimonthPlan] || null : null;

  const getPlanName = () => {
    if (starterPack) return 'Starter Pack';
    if (doseType === 'micro') return '10 Week Plan';
    return `${multimonthPeriod} Plan`;
  };

  const productImage =
    currentProductName == AvailableProducts.Lover
      ? ImagesMapping[currentProductName]
      : ImagesMapping[currentProductName][subProductName.replace('_micro', '')];

  return (
    <>
      <figure className="h-[180px] lg:h-[240px] mb-4">
        <img className="h-full w-auto" src={productImage} alt="Product" />
      </figure>
      <div className="text-center">
        <Badge variant={currentProductName} className="mb-2">
          {protocolNames[currentProductName]}
        </Badge>
        {currentProductName == AvailableProducts.Lover && <h4>Vardenafil + Tadalafil</h4>}
        <h4>
          {doseType === 'micro' ? 'Microdose - ' : ''}
          {subProductNames[currentProductName][subProductName]}
        </h4>
      </div>

      <hr />

      <div className={cx({ 'first-installment-discount': firstInstallmentDiscount })}>
        <div className="flex flex-row justify-between items-center">
          <h5>
            {getPlanName()}
            {multimonthPlan && doseType === 'standard' && (
              <span className="paragraph-light-normal">
                {' '}
                ({multimonthPlan} {multimonthPlan === 1 ? 'month' : 'months'})
              </span>
            )}
          </h5>
          <PriceFormatter
            price={priceCalculator.monthlyCost()}
            className="font-medium"
            period={firstInstallmentDiscount || starterPack ? '' : 'mo'}
            free={firstInstallmentDiscount && [12, 3].includes(multimonthPlan)}
            discountPrice={
              firstInstallmentDiscount && multimonthPlan === 3 ? (
                <PriceFormatter
                  price={(priceCalculator.fullMonthlyCost() - priceCalculator.totalDiscountsValue()) as number}
                  className="font-medium"
                />
              ) : undefined
            }
          />
        </div>
        {firstInstallmentDiscountText && (
          <p className="mt12 mb12 font-bold">{firstInstallmentDiscountText} off your first month</p>
        )}
        {starterPack ? (
          <p className="my-3">
            Starter Pack includes {multimonthPlan} months of treatment for one low price. Starter Packs are designed for
            new patients and cover only the three lowest available doses. To start at a higher dose, sign up for a
            monthly or quarterly plan.
          </p>
        ) : (
          <p className="my-3">
            {firstInstallmentDiscount
              ? firstInstallmentDescriptionText[multimonthPlan]
              : 'Covers cost of prescription medication (if qualified), doctor consultation, and psychologist-led health coaching.'}
          </p>
        )}
      </div>
      {checkoutHelper.enableBuildingBlocks() && (
        <GenericBuildingBlocks
          currentProductName={currentProductName}
          starterPack={starterPack}
          supplementsOnAnotherIntake={supplementsOnAnotherIntake}
          multimonthPlan={multimonthPlan}
          priceCalculator={priceCalculator}
          bbOutOfStock={checkoutHelper.bbOutOfStock()}
          activeProductWithSupplementName={activeProductWithSupplementName}
          isOnboarding={isOnboarding}
        />
      )}
      {isOnboarding && (
        <GenericOnboardingServices
          firstInstallmentDiscount={firstInstallmentDiscount}
          multimonthPlan={multimonthPlan}
        />
      )}
      <SubscriptionSummaryComponent
        starterPack={starterPack}
        multimonthPeriod={multimonthPeriod}
        currentProductName={currentProductName}
        multimonthPlan={multimonthPlan}
        priceCalculator={priceCalculator}
        isOnboarding={isOnboarding}
        productChanged={productChanged}
        nextBillingCycle={nextBillingCycle(currentProductName)}
        doseType={doseType}
      />
    </>
  );
};

export default SubscriptionWidget;
